import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Ticket : famille "demande entrante prise en charge".
 *
 * Demande, réclamation ou signalement reçu de l'extérieur, pris en charge par
 * étapes jusqu'à résolution, avec une priorité et des délais (SLA). Construite
 * sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("réclamation", "panne",
 * "question") : il connaît la famille générique `ticket`. Le vocabulaire métier
 * est porté par le champ `type` (chaîne libre déclarée par l'app) ; l'avancement
 * (`status`), l'urgence (`priority`) et le canal d'arrivée (`channel`) sont des
 * champs métier à part, chaînes libres pilotées par l'app.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `contactId` (le demandeur) et `assignedWorkerId` (l'intervenant en charge),
 * tous deux optionnels, un signalement entrant pouvant précéder son contact et
 * n'être encore assigné à personne. Les liens work-order, task et message ne
 * vivent PAS ici : c'est l'autre entité qui porte un `ticketId` (l'ordre de
 * travail, la tâche ou le message pointent le ticket dont ils découlent).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille ticket (hors champs standard du socle). */
export const ticketCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reclamation", "panne"). */
  type: z.string().min(1),
  /** Demandeur à l'origine du ticket (référence `contact`), optionnel : une demande entrante peut précéder son contact. */
  contactId: z.string().min(1).optional(),
  /** Urgence, chaîne libre pilotée par l'app (ex. "basse", "normale", "haute", "urgente"). Optionnel. */
  priority: z.string().min(1).optional(),
  /** État d'avancement, chaîne libre pilotée par l'app (ex. "ouvert", "en_cours", "resolu", "clos"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Intervenant en charge (référence `worker`), optionnel : un ticket peut n'être assigné à personne. */
  assignedWorkerId: z.string().min(1).optional(),
  /** Échéance de traitement contractuelle (SLA) à ne pas dépasser. Optionnel. */
  slaDueAt: z.date().optional(),
  /** Canal d'arrivée de la demande (ex. "email", "telephone", "web", "guichet"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Résumé de la résolution apportée, renseigné à la clôture. Optionnel. */
  resolutionSummary: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille ticket (champs standard + champs métier). */
export const TicketSchema = z.object({
  ...baseEntityFields,
  ...ticketCoreFields,
});

/** Type d'un ticket. */
export type Ticket = z.infer<typeof TicketSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const TicketCreateInputSchema = z.object({
  ...ticketCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un ticket. */
export type TicketCreateInput = z.infer<typeof TicketCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const TicketUpdateInputSchema = z
  .object({ ...ticketCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un ticket. */
export type TicketUpdateInput = z.infer<typeof TicketUpdateInputSchema>;
